import { AddressRequest } from '../../types/address';
import {
  CreateOrUpdateSalesDocumentRequest,
  ManualTermsOfPaymentRequest,
} from '../../types/create-or-update-sales-document-request';
import { DocumentFormData } from '../../types/document-form-data';

function shouldSendManualAddress(data: DocumentFormData): boolean {
  if (!data.manualAddress?.trim()) return false;
  if (data.manualAddressEnabled) return true;
  const deliveryType = (data.deliveryType ?? '').toLowerCase();
  const isPickupOrExpress =
    !deliveryType ||
    deliveryType.startsWith('abholung') ||
    deliveryType.includes('holt selbst') ||
    deliveryType.startsWith('express');
  return !isPickupOrExpress;
}

function resolveManualDeliveryAddress(data: DocumentFormData): string | null {
  if (data.manualConstructionSiteEnabled && data.constructionSiteAddress?.trim())
    return data.constructionSiteAddress;
  return shouldSendManualAddress(data) ? data.manualAddress : null;
}

function toManualTermsOfPayment(
  data: DocumentFormData
): ManualTermsOfPaymentRequest | undefined {
  if (!data.manualTermsOfPaymentEnabled) return undefined;
  const terms = data.manualTermsOfPayment;
  if (!terms || terms.days1 == null || terms.days2 == null || terms.dueDays == null) {
    return undefined;
  }
  return {
    cashDiscount1: terms.cashDiscount1,
    cashDiscount2: terms.cashDiscount2,
    days1: terms.days1,
    days2: terms.days2,
    dueDays: terms.dueDays,
  };
}

function toDeliveryAddressRequest(data: DocumentFormData): AddressRequest | undefined {
  if (!data.deliveryAddressManualEnabled) return undefined;
  const address = data.deliveryAddress;
  if (!address) return undefined;
  const value = (v: string | null): string | undefined => v?.trim() || undefined;
  const request: AddressRequest = {
    salutation: value(address.salutation),
    name1: value(address.name1),
    name2: value(address.name2),
    name3: value(address.name3),
    street: value(address.street),
    countryCode: value(address.countryCode),
    postCode: value(address.postCode),
    city: value(address.city),
    mail1: value(address.mail1),
  };
  return Object.values(request).some((v) => v !== undefined) ? request : undefined;
}

function refId(value: object | null | undefined): string | null {
  const v = value as { id?: unknown; key?: unknown } | null | undefined;
  const id = v?.id ?? v?.key;
  return id != null ? String(id) : null;
}

function stripNullish<T extends object>(obj: T, keepNulls: boolean): T {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) continue;
    if (value === null && !keepNulls) continue;
    result[key] = value;
  }
  return result as T;
}

export function stripUnchangedNulls(
  body: CreateOrUpdateSalesDocumentRequest,
  baseline: CreateOrUpdateSalesDocumentRequest | null
): CreateOrUpdateSalesDocumentRequest {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(body)) {
    if (value === null) {
      const prev = baseline
        ? (baseline as Record<string, unknown>)[key]
        : undefined;
      if (prev == null) continue;
    }
    result[key] = value;
  }
  return result as CreateOrUpdateSalesDocumentRequest;
}

export function formDataToCreateRequest(
  data: DocumentFormData,
  documentTypeCode?: string | null,
  mode: 'patch' | 'create' = 'patch',
  isDraft = false
): CreateOrUpdateSalesDocumentRequest {
  const customerId =
    data.constructionSiteId != null
      ? String(data.constructionSiteId)
      : data.customer?.id != null
        ? String(data.customer.id)
        : null;
  const destinationDocumentType: string | null =
    isDraft && documentTypeCode && documentTypeCode !== 'DRAFT'
      ? documentTypeCode
      : null;
  return stripNullish<CreateOrUpdateSalesDocumentRequest>(
    {
      customerId,
      documentType:
        mode === 'create' && !isDraft ? (documentTypeCode ?? null) : undefined,
      destinationDocumentType: isDraft ? destinationDocumentType : undefined,
      distributionTypeId:
        data.distributionTypeId != null ? String(data.distributionTypeId) : undefined,
      header: data.header,
      footer: data.footer,
      collector: data.collector,
      constructionSite: data.constructionSite ?? null,
      deliveryDate: data.deliveryDateFrom
        ? { from: data.deliveryDateFrom, to: data.deliveryDateTo }
        : null,
      serviceDate: data.serviceDate?.from ?? null,
      deliveryTime: data.deliveryTime ? { from: data.deliveryTime, to: null } : null,
      deliveryWeek: data.calendarWeek,
      deliveryHint: data.deliveryNote,
      meansOfTransport: data.transportMeans,
      vehicleAllocation: data.vehicleAllocation,
      taxTreatment: data.taxTreatment ?? undefined,
      taxCalculation: data.taxCalculation ?? undefined,
      paymentInterval: data.paymentInterval ?? undefined,
      validUntil: data.validUntil,
      forTheAttentionOf: data.attention,
      manualDeliveryAddress: resolveManualDeliveryAddress(data),
      representative1Id: refId(data.representative), // Vertreter 1
      representative2Id: refId(data.representative2), // Vertreter 2
      siteEngineerId: refId(data.siteEngineer), // Bauleiter
      driverId: refId(data.driver), // Fahrer
      vehicleId: refId(data.vehicle), // Fahrzeug
      projectId: refId(data.object), // Objekt
      termsOfPaymentId: data.manualTermsOfPaymentEnabled
        ? undefined
        : refId(data.paymentCondition), // Zahlungskondition
      manualTermsOfPayment: toManualTermsOfPayment(data),
      paymentMethodId: refId(data.paymentMethod), // Zahlungsart
      costCenterId: data.costCenter ?? null, // Kostenstelle (name = id)
      stockId: refId(data.stock), // Lager
      storageSiteId: refId(data.storageSite), // Lagerort
      countryId: refId(data.country), // Land
      externalPurchaseOrderNumber: data.externalOrderNumber,
      externalPurchaseOrderDate: data.externalOrderDate,
      note: data.note, // Vermerk (Intern)
      approvalForInvoicing: data.approvalForInvoicing, // Fakturenfreigabe
      deliveryAddress: toDeliveryAddressRequest(data),
    },
    mode === 'patch'
  );
}
